import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import tokens from "@/theme/tokens";
import radii from "@/theme/radii";

/**
 * Replaces profile.scss (1,162 lines).
 *
 * Typed as SystemStyleObject rather than SxProps so these compose with object
 * spread — SxProps is a union that includes arrays and cannot be spread.
 *
 * The layout profile.scss described is kept: a dark left rail beside a white
 * content column. Everything inside it is expressed the way the newest pages
 * express it (Pages/Project/Settings, Pages/Hubs/HubSettings) — palette slots
 * (`secondary.main`, `primary.light`, …) where a value already lives on the
 * theme, Typography variants rather than bare tags, and MUI controls rather
 * than hand-styled ones. Where the old stylesheet's literal didn't already
 * map onto the palette or the theme's 8px spacing scale, Wave 0 relocated it
 * to a `tokens.*`/`radii.*` reference instead — verbatim px strings, hex
 * colours, and em/rem sizes, not palette slots or the 8px scale.
 * Reconciling those tokens onto the palette and the 8px scale is Wave 1's
 * job. What remains here is layout and the few decorative shapes MUI has no
 * component for; the rest moved into the components as props.
 *
 * Breakpoints: $break-s/m/l/xl in _variables.scss are the theme's sm/md/lg/xl
 * exactly (475 / 725 / 1050 / 1250), so those map onto theme breakpoints.
 */

/**
 * MUI's convention for a divider on a dark surface. The stylesheet used
 * $color-grayDark (#747474) here, which reads as a hard line against
 * secondary.main; a translucent white sits on the surface instead of cutting
 * across it, and needs no palette entry of its own.
 */
const RAIL_DIVIDER = tokens.color.railDivider;

/* ------------------------------------------------------------------ shell */

/** .userProfile */
export const pageSx: SystemStyleObject<Theme> = {
  minHeight: "calc(100vh - 50px)",
  maxWidth: "100%",
  m: 0,
  bgcolor: "common.white",
};

/** .userProfile__wrap */
export const pageWrapSx: SystemStyleObject<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  flexWrap: "nowrap",
  alignItems: "stretch",
};

/** .bioInfo — the dark left rail. */
export const sideBarSx: SystemStyleObject<Theme> = {
  bgcolor: "secondary.main",
  width: { xs: "100%", sm: 250, lg: 300 },
  flexShrink: 0,
  minHeight: { sm: "calc(100vh - 50px)" },
  pb: { xs: 2, sm: 4 },
};

/** .contentInfo — the white right column. */
export const contentSx: SystemStyleObject<Theme> = {
  width: "100%",
  minWidth: 0,
  position: "relative",
  bgcolor: "common.white",
};

/**
 * One block in the rail. The stylesheet gave each of these its own padding
 * (10px 20px, 18px 20px, 10px 0) and hung 1px borders off .socialMedia and
 * .visibility__cont; one padded block plus a Divider between them replaces
 * both.
 */
export const railSectionSx: SystemStyleObject<Theme> = {
  px: { xs: 2, lg: 3 },
  py: 2,
};

/** The line between two rail blocks. */
export const railDividerSx: SystemStyleObject<Theme> = {
  borderColor: RAIL_DIVIDER,
  mx: { xs: 2, lg: 3 },
};

/**
 * .about / .judge / .experience / .no_permission__cont — the @mixin
 * contentInfoWrap. Its five stepped max-widths (555 → 1300) become the one
 * measure the newest pages use, and its 20px padding the theme's scale.
 */
export const contentPanelSx: SystemStyleObject<Theme> = {
  maxWidth: 960,
  mx: "auto",
  px: { xs: 2, md: 4 },
  py: { xs: 3, md: 4 },
  overflowWrap: "break-word",
};

/**
 * .userProfile__edit — the title/action row above every editable panel. The
 * stylesheet put `cursor: pointer` on the whole strip while only the pen was
 * clickable; the pointer now belongs to the button.
 */
export const panelHeadSx: SystemStyleObject<Theme> = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 1,
  width: "100%",
  mb: 2,
};

/* --------------------------------------------------------- progress bar */

/** .profile-prog */
export const progressSx: SystemStyleObject<Theme> = {
  bgcolor: "background.default",
  px: { xs: 2, md: 3 },
  py: 1.5,
};

/** .profile-prog__bar + .profile-prog__bar-fill */
export const progressBarSx: SystemStyleObject<Theme> = {
  flexGrow: 1,
  maxWidth: 240,
  height: 8,
  borderRadius: `${radii.sm}px`,
  // the track sits on grey.50, so white would leave the empty part invisible
  bgcolor: "grey.300",
  "& .MuiLinearProgress-bar": { bgcolor: tokens.color.accentTeal, borderRadius: `${radii.sm}px` },
};

/* -------------------------------------------------------- no permission */

/** .no_permission */
export const noPermissionSx: SystemStyleObject<Theme> = {
  minHeight: "calc(100vh - 50px)",
  backgroundImage: "url('/images/empty_profile_permission.png')",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "15% center",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

/** .no_permission__wrap — $color-secondary at 90%. */
export const noPermissionWrapSx: SystemStyleObject<Theme> = {
  bgcolor: tokens.color.profileNoPermissionScrim,
  color: "common.white",
  width: "100%",
};

/* -------------------------------------------------------------- bio info */

/** .bioInfo__pic */
export const avatarSx: SystemStyleObject<Theme> = {
  display: "block",
  width: "100%",
  height: "auto",
};

export const avatarWrapSx: SystemStyleObject<Theme> = {
  position: "relative",
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",

  cursor: "pointer",

  "& img": {
    transition: "opacity 200ms",
  },

  "&:hover img": {
    opacity: 0.3,
  },

  "&:hover .MuiButton-root": {
    opacity: 1,
  },
};

/** .bioInfo__pic__wrap-public — same box, but nothing reacts to hover. */
export const avatarWrapPublicSx: SystemStyleObject<Theme> = {
  position: "relative",
  width: "100%",

  display: "flex",
  justifyContent: "center",
  alignItems: "center",

};

/** .bioInfo__pic__edit */
export const avatarEditSx: SystemStyleObject<Theme> = {
  position: "absolute",
  top: "45%",
  left: "35%",
  opacity: 0,
  transition: "opacity 200ms",
  whiteSpace: "nowrap",
};

/* ---------------------------------------------------- controls in the rail */

/**
 * A TextField on secondary.main. The theme styles inputs for a white page, so
 * the parts that would come out near-invisible are restated here rather than
 * in each call site.
 */
export const railFieldSx: SystemStyleObject<Theme> = {
  "& .MuiOutlinedInput-root": {
    color: "common.white",
    "& .MuiOutlinedInput-notchedOutline": { borderColor: RAIL_DIVIDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "grey.400" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: tokens.color.accentTeal },
  },
  "& .MuiInputBase-input::placeholder": { color: "grey.500", opacity: 1 },
};

/**
 * The public/private switch. primary.main (#007380) has too little contrast
 * against secondary.main, so the track and thumb take tokens.color.accentTeal
 * — the accent the hexagon toggle this replaces was already using.
 */
export const railSwitchSx: SystemStyleObject<Theme> = {
  "& .MuiSwitch-track": { bgcolor: "grey.600", opacity: 1 },
  "& .MuiSwitch-thumb": { color: "common.white" },
  "& .Mui-checked + .MuiSwitch-track": { bgcolor: tokens.color.accentTeal, opacity: 1 },
  "& .Mui-checked .MuiSwitch-thumb": { color: "common.white" },
};

/* --------------------------------------------------------- social links */

/** .socialMedia__social-links */
export const socialListSx: SystemStyleObject<Theme> = {
  display: "flex",
  flexDirection: { xs: "row", lg: "column" },
  flexWrap: "wrap",
  alignItems: { xs: "center", lg: "stretch" },
  gap: 0.5,
};

/** .socialMedia__social-links__cont used as a button (with .btn-link). */
export const socialLinkButtonSx: SystemStyleObject<Theme> = {
  minWidth: 0,
  justifyContent: "flex-start",
  // primary.main and tokens.color.accentTealDeep are both too dark to read
  // on secondary.main; primary.light is the palette's one bright teal
  color: "primary.light",
  px: 1,
  py: 0.5,
  "&:hover": { bgcolor: RAIL_DIVIDER, color: "common.white" },
};

/** .socialMedia__social-links__icon */
export const socialIconSx: SystemStyleObject<Theme> = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  flexShrink: 0,
  "& img": { display: "block", height: 20, width: "auto" },
};

/** .socialMedia__social-links__user */
export const socialHandleSx: SystemStyleObject<Theme> = {
  display: { xs: "none", sm: "block" },
  overflowWrap: "break-word",
  textAlign: "left",
  minWidth: 0,
};

/* ------------------------------------------------------------- projects */

/** .project a — the rail's project list. */
export const projectItemSx: SystemStyleObject<Theme> = {
  color: "primary.light",
  px: 1,
  py: 1,
  borderRadius: `${radii.sm}px`,
  cursor: "pointer",
  "&:hover": { color: "common.white", bgcolor: RAIL_DIVIDER },
};

/* -------------------------------------------------------- about / judge */

/** .about__empty / .judge__empty */
export const panelEmptySx: SystemStyleObject<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", lg: "row" },
  alignItems: "center",
  justifyContent: "center",
  gap: 3,
  "& img": { maxWidth: 500, width: "100%", height: "auto" },
};

/** .about__empty__prompt / .judge__empty__prompt */
export const panelEmptyPromptSx: SystemStyleObject<Theme> = {
  maxWidth: 240,
  textAlign: { xs: "center", lg: "left" },
};

/* ----------------------------------------------------------- experience */

/** .experience__sect — one entry, hung off a dashed timeline rule. */
export const experienceEntrySx: SystemStyleObject<Theme> = {
  position: "relative",
  borderLeft: "1px dashed",
  borderColor: "grey.400",
  pl: 3,
  pr: 1,
  pb: 4,
};

/**
 * .experience__sect__hexagon — the marker on the timeline rule. The
 * stylesheet drew a hexagon out of one box and two borders; at 10px the shape
 * never read as one, so it is a dot.
 */
export const experienceMarkerSx: SystemStyleObject<Theme> = {
  position: "absolute",
  left: -5,
  top: 6,
  width: 10,
  height: 10,
  borderRadius: radii.circle,
  bgcolor: tokens.color.accentTeal,
};

/** .experience__sect__editing — the same box while it is being edited. */
export const experienceFormSx: SystemStyleObject<Theme> = {
  position: "relative",
  borderLeft: "2px solid",
  borderColor: "secondary.main",
  pl: 3,
  pr: 1,
  pb: 4,
};

/** .experience__fields__date-wrap */
export const experienceDateRowSx: SystemStyleObject<Theme> = {
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  gap: 2,
};

/* --------------------------------------------------- avatar upload modal */

/** .modal + .image-upload, from components/Common/Modal/modal.scss. */
export const uploadModalSx: SystemStyleObject<Theme> = {
  maxWidth: "100%",
  p: 1,
};

/** .image-upload__img-wrapper + .img-wrapper--avatar */
export const uploadPreviewSx: SystemStyleObject<Theme> = {
  m: "0 auto",
  overflow: "hidden",
  "& img": { display: "block", m: "auto", maxWidth: "100%" },
};
